import React from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LoginPage() {
  const location = useLocation();
  // Flag passato da AuthCallback in caso di 403 dal backend (email non
  // invitata, security fix 2026-05-29). Mostra banner amichevole.
  const unauthorized = location.state?.unauthorized;
  const unauthorizedMsg = typeof unauthorized === 'string'
    ? unauthorized
    : 'Accesso non autorizzato. Contatta il tuo Operations Manager per ricevere un invito.';

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md space-y-8">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-black flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">S</span>
              </div>
              <span className="font-heading font-bold text-xl tracking-tight">StoreOps</span>
            </div>
            <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">
              Accedi alla piattaforma
            </h1>
            <p className="mt-3 text-slate-500 text-base">
              Piattaforma operativa di controllo performance e redditivit&agrave; store
            </p>
          </div>
          <div className="space-y-4">
            {unauthorized && (
              <div
                className="border border-red-300 bg-red-50 p-4 flex items-start gap-3"
                role="alert"
                data-testid="login-unauthorized-banner"
              >
                <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-900">Accesso negato</p>
                  <p className="text-xs text-red-800 mt-1 leading-relaxed">{unauthorizedMsg}</p>
                </div>
              </div>
            )}
            <Button
              data-testid="google-login-btn"
              onClick={handleLogin}
              className="w-full h-12 bg-black text-white hover:bg-slate-900 rounded-none text-base font-medium"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Accedi con Google
            </Button>
            <details className="group" data-testid="switch-account-help">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-800 transition-colors list-none flex items-center gap-1">
                <span className="underline underline-offset-2">Vuoi accedere con un altro account Google?</span>
              </summary>
              <div className="mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 p-3 leading-relaxed space-y-1.5">
                <p>Google ripropone automaticamente l'ultimo account usato nel browser. Per cambiarlo:</p>
                <p>1. Apri <a href="https://myaccount.google.com" target="_blank" rel="noreferrer" className="underline font-medium">myaccount.google.com</a> e passa all'account desiderato (icona profilo in alto a destra → scegli o aggiungi account), poi torna qui e accedi.</p>
                <p>2. In alternativa usa un altro profilo Chrome o una finestra di navigazione in incognito.</p>
              </div>
            </details>
          </div>
          <div className="pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-400">
              Piattaforma riservata. Accesso consentito solo agli utenti autorizzati.
            </p>
          </div>
        </div>
      </div>
      <div className="hidden lg:block lg:w-1/2 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-12">
            <h2 className="font-heading text-3xl font-bold text-white tracking-tight mb-4">
              Store Performance Platform
            </h2>
            <p className="text-slate-400 text-lg max-w-md">
              Controllo KPI, analisi redditivit&agrave; e gestione operativa dei punti vendita
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6">
              {['Little Italy', 'Il Barbiere', 'Trilab'].map((b) => (
                <div key={b} className="border border-slate-700 p-4">
                  <p className="text-white font-medium text-sm">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
